import * as React from 'react';

import { BlurGlow } from '@/components/shared/blur-glow';
import { cn } from '@/lib/utils';

const CARD_SURFACE_CLASS =
  'relative isolate overflow-hidden border border-border-subtle bg-card transition-[border-color,box-shadow] duration-200 hover:border-border hover:shadow-e1';

const CARD_GLOW_CLASS =
  'pointer-events-none absolute top-0 right-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 overflow-hidden opacity-75';

const CARD_SOLID_CLASS =
  'relative isolate overflow-hidden border border-border-subtle bg-card';

interface CardProps extends React.ComponentProps<'div'> {
  flat?: boolean;
  nested?: boolean;
  variant?: 'solid' | 'glow';
}

function Card({
  className,
  flat = false,
  nested = false,
  variant = 'solid',
  children,
  ...props
}: CardProps) {
  if (flat) {
    return (
      <div
        data-slot="card"
        className={cn(
          'flex flex-col gap-5 rounded-xl border border-border-subtle bg-card py-5 text-foreground',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      data-slot="card"
      className={cn(
        'flex flex-col rounded-xl py-5 text-foreground',
        variant === 'glow' ? CARD_SURFACE_CLASS : CARD_SOLID_CLASS,
        nested && 'backdrop-blur-none',
        className,
      )}
      {...props}
    >
      {variant === 'glow' && !nested ? (
        <BlurGlow
          color="color-mix(in srgb, var(--primary) 14%, transparent)"
          className={CARD_GLOW_CLASS}
        />
      ) : null}
      <div className="relative z-[1] flex flex-col gap-6">{children}</div>
    </div>
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1 px-5 has-data-[slot=card-action]:grid-cols-[1fr_auto]',
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn('text-base leading-6 font-semibold tracking-[-0.005em] text-foreground', className)}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-[13px] leading-[18px] text-muted-foreground', className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-5', className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-5', className)}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
